import { Component, HostListener, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Campo } from "../../models/coleta";
import { AppState, Form } from "../../models/app-state";
import { AppFlowService } from "../../services/app-flow.service";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { AddressService } from "../../services/address.service";
import { MatDialog } from "@angular/material/dialog";
import { DialogComponent } from "src/app/components/dialog/dialog.component";
import { ReRecaptchaSmileGoService } from "src/app/services/recaptcha-smilego.service";
import { concatMap, take, tap } from "rxjs/operators";
import { of } from "rxjs/internal/observable/of";
import { SessionStorageService } from "src/app/services/session-storage.service";
import { CaptchaStorageService } from "src/app/services/captcha-storage.service";
import { AppValidators } from "src/app/components/dynamic-inputs/util/validators";
import { STORAGE_KEYS } from "src/app/components/dynamic-inputs/util/constants";
import { equalsPhonesErrorKey } from "src/app/components/dynamic-inputs/util/equalsPhones.validator";
import { TURNSTILE_FIELD_NAME } from "src/app/components/turnstile/ngx-turnstile.component";
import { TokenDispatcherService } from "src/app/services/token-dispatcher.service";
import { PROPRIEDADES } from 'src/app/components/dynamic-inputs/util/constants';

@Component({
  selector: "app-form-page",
  templateUrl: "./form-page.component.html",
  styleUrls: ["./form-page.component.scss"],
})
export class FormPageComponent implements OnInit, OnDestroy {
  campos: Campo[] = [];
  form: Form = {};
  formGroup: UntypedFormGroup;
  isLoading = false;
  title: string | undefined = "";
  description: string | undefined = "";
  isSubmited = false;
  isCepLoading = false;
  captchaProvider: string | undefined;
  captchaResponse: string | null = '';
  captchaKey: string = '';
  showCaptcha = false;
  captchaInvisible = false;
  captchaValidating = false;
  captchaError = false;

  isResendingSms = false;
  resendCooldown = 0;
  private resendTimer: any;

  constructor(
    private actRoute: ActivatedRoute,
    private addressService: AddressService,
    private router: Router,
    private fb: UntypedFormBuilder,
    private appFlowService: AppFlowService,
    public dialog: MatDialog,
    private recaptchaSmileGoService: ReRecaptchaSmileGoService,
    private sessionStorageService: SessionStorageService,
    private captchaStorage: CaptchaStorageService,
    private tokenDispatcherService: TokenDispatcherService
  ) {
    this.formGroup = this.fb.group({});
  }

  isValid(): boolean {
    const invalidEntries = Object.keys(this.formGroup.controls).filter(
      (key) => !this.formGroup.controls[key]?.valid
    );

    // @ts-ignore
    const valid = !this.campos.some((el) => invalidEntries.includes(el.codigo));

    return valid &&
          !this.isLoading &&
          !this.hasEqualsPhonesError() &&
          !this.captchaError;
  }

  hasEqualsPhonesError(): boolean {
    return this.formGroup.hasError(equalsPhonesErrorKey);
  }

  getEqualsPhonesError(): string {
    return this.formGroup.getError(equalsPhonesErrorKey);
  }

  async ngOnInit() {
    this.actRoute.data.subscribe((data: any) => {
      const appState: AppState = data.appState;

      this.campos = appState.field.fields;
      this.title = appState.field.title;
      this.description = appState.field.description;
      this.form = appState.form;

      const telefoneCompleto = this.sessionStorageService.getSessionEntry(
        STORAGE_KEYS.TELEFONE_PESSOA
      );
      if (telefoneCompleto) {
        this.formGroup.setValidators(
          AppValidators.equalsPhones(telefoneCompleto)
        );
        this.formGroup.updateValueAndValidity();
      }

      if (this.hasSmsField()) {
        this.startInitialCooldown();
      }
    });

    const envConfig = await this.sessionStorageService.getEnvConfig();
    
    this.captchaProvider = envConfig.captchaProvider;
    this.captchaKey = envConfig.captchaKey ?? '';
    this.showCaptcha = await this.shouldShowCaptcha();
    
    // Captcha invisível quando tiver telefone preenchido e verificação SMS ativa
    this.captchaInvisible = !this.isPreProposta() && this.hasPhoneField() && (envConfig.verificacaoSms ?? false);
    
    // Feedback visual pra validação captcha invisível
    this.formGroup.statusChanges.subscribe(() => {
      if (this.captchaInvisible && this.formGroup.valid && !this.captchaResponse && this.showCaptcha) {
        this.captchaValidating = true;
      }
    });
  }

  //Trata eventos emitidos no componente filho dynamic-input, quando houver um CEP digitado com 8 digitos.
  onLoadedCep(cep: string) {
    this.isCepLoading = true;

    this.addressService
      .fetchCep(cep)
      .then((endereco) => {
        //Irá verificar se existe alguma propriedade da interface de endereco que corresponde aos nomes dos formcontrols.
        //Exemplo: Se o nome do control for pessoa.endereco.CIDADE, a funcao abaixo irá preencher automaticamente, pois CIDADE é uma das props de endereco.
        Object.keys(this.formGroup.controls).forEach((controlKey) => {
          Object.keys(endereco).forEach((key) => {
            this.isCepLoading = false;

            if (key === "cep") return;
            if (controlKey.includes(key)) {
              const dynamicAdressValue = Object.entries(endereco).find(
                (array) => array[0] === key
              )?.[1];

              this.formGroup.controls[controlKey].patchValue(
                dynamicAdressValue
              );
            }
          });
        });
      })
      .catch((error) => {
        this.isCepLoading = !this.isCepLoading;
        this.dialog.open(DialogComponent, {
          width: "250px",
          data: {
            title: "Oooops!",
            message:
              "Não foi possível achar este endereço em nossa base de dados.",
          },
        });
      });
  }

  // @HostListener('document:keydown', ['$event'])
  // handleKeyboardEvent(event: KeyboardEvent): void {
  //   if (event.key === 'Enter' && this.isValid()) {
  //     this.changeStep();
  //   }
  // }

  sendCaptchaResponse(captchaResponse: string | null) {
    this.captchaValidating = false;
    if(captchaResponse) {
      this.captchaResponse = captchaResponse;
      this.captchaStorage.setToken(captchaResponse);
      this.captchaError = false;
    } else {
      this.captchaError = true;
    }
  }

  handleCaptchaError(error: string | null) {
      this.captchaValidating = false;
      this.captchaError = true;
      console.error(error);
  }

  isPreProposta() {
    const appState = this.appFlowService.getAppState?.();
    if (!appState || !appState.pages) {
      console.warn('[isValid] App state not initialized yet', appState);
      return false;
    }

    return appState.pages.some(page => /pre-proposta/ig.test(page));
  }

  hasPhoneField(): boolean {
    return this.campos.some(
      campo => campo.codigo === 'pessoa.telefones.numero' || campo.codigo === 'pessoa.telefones.ddd'
    );
  }

  hasSmsField(): boolean {
    return this.campos.some(campo => campo.codigo === 'termo.aceite.token.sms');
  }

  async shouldShowCaptcha(): Promise<boolean> {
    if (this.isPreProposta()) {
      return true;
    }
    
    // Validação de campo de telefone e verificação SMS ativada
    if (this.hasPhoneField()) {
      const envConfig = await this.sessionStorageService.getEnvConfig();
      return envConfig.verificacaoSms ?? false;
    }
    
    return false;
  }

  isRecaptchaOfTypeTurnstileAndInvalid(){
    return this.showCaptcha && this.captchaProvider === 'turnstile' && !(!!this.captchaResponse);
  }


    async resendSms(): Promise<void> {
      if (this.resendCooldown > 0 || this.isResendingSms) return;
  
      const telefoneCompleto = this.sessionStorageService.getSessionEntry(
        STORAGE_KEYS.TELEFONE_PESSOA
      );
  
      if (!telefoneCompleto) {
        this.dialog.open(DialogComponent, {
          width: '250px',
          data: {
            title: 'Erro',
            message: 'Telefone não encontrado. Por favor, preencha o telefone novamente.',
          },
        });
        return;
      }
  
      this.isResendingSms = true;
  
      try {

        const propostaUUID = this.appFlowService.getAppState()?.proposta?.id;
        const stepNameToken = this.appFlowService.getAppState()?.proposta?.etapaAtual?.nome;
        
        const result = await this.tokenDispatcherService
          .sendToken(telefoneCompleto, stepNameToken, propostaUUID)
          .toPromise();
  
  
        if (!result?.success) {
          throw new Error(result?.message || 'Erro ao reenviar código SMS');
        }
  
        this.dialog.open(DialogComponent, {
          width: '250px',
          data: {
            title: 'Sucesso',
            message: result?.message || 'Código SMS reenviado com sucesso.',
          },
        });
  
        this.startResendCooldown();
  
      } catch (error: any) {
        console.error('Erro ao reenviar SMS:', error);
  
        const errorMessage = error?.message || 'Erro ao reenviar código SMS. Tente novamente.';
  
        this.dialog.open(DialogComponent, {
          width: '250px',
          data: {
            title: 'Erro',
            message: errorMessage,
          },
        });
      } finally {
        this.isResendingSms = false;
      }
    }
  
    private startInitialCooldown(): void {
      this.resendCooldown = 60;
      if (this.resendTimer) {
        clearInterval(this.resendTimer);
      }
      this.resendTimer = setInterval(() => {
        this.resendCooldown--;
        if (this.resendCooldown <= 0) {
          clearInterval(this.resendTimer);
          this.resendCooldown = 0;
        }
      }, 1000);
    }

    private startResendCooldown(): void {
      this.startInitialCooldown();
    }

  async changeStep() {

    if(!this.isValid() || this.isRecaptchaOfTypeTurnstileAndInvalid()) return;

    const formGroupValues = this.formGroup.getRawValue();

    let formValues: any = this.campos.reduce((acc, campo) => {
      if (campo.codigo) {
        return {
          ...acc,
          [campo.codigo]: formGroupValues[campo.codigo],
        };
      }
      return {
        ...acc,
      };
    }, {});

    // Remover caracteres de máscara (pontuação) para campos que possuem máscara
    // antes de enviar para o backend. Mantemos apenas caracteres alfanuméricos.
    this.campos.forEach((campo) => {
      if (!campo || !campo.codigo) return;
      const hasMask = campo.atributos?.some((item) => item.propriedade === PROPRIEDADES.MASCARA);
      if (hasMask && formValues[campo.codigo] && typeof formValues[campo.codigo] === 'string') {
        formValues[campo.codigo] = formValues[campo.codigo].toString().replace(/[^A-Za-z0-9]/g, '');
      }
    });

    const dddPessoa = formGroupValues["pessoa.telefones.ddd"];
    const telefonePessoa = formGroupValues["pessoa.telefones.numero"];
    if (telefonePessoa && dddPessoa) {
      const telefoneCompleto = `${dddPessoa}${telefonePessoa}`;
      this.sessionStorageService.saveSessionEntry(
        STORAGE_KEYS.TELEFONE_PESSOA,
        telefoneCompleto
      );
    }

    this.isLoading = true;
    const stepName = await this.appFlowService.changeStep(formValues);
    this.isLoading = false;
    this.isSubmited = false;

    await this.router.navigate([stepName]);
  
  }

  ngOnDestroy(): void {
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
    }
  }

}
